import type { CSSProperties } from 'react';

import { Skin, Sport } from '#src/__generated__/globalTypes.ts';
import { SetColors } from '#src/atoms/icons/SetColors.tsx';

import { SetLogoImg } from './assets/SetLogoImg.tsx';
import mobileDecoration from './assets/board/mobile-decoration.webp';
import commonPacksLogoImage from './assets/colors-logo.svg';
import cardRevealSwipeIconImage from './assets/colors-series-icon.svg';
import footballComposeTeam from './assets/compose-team/FootballFieldBackground.webp';
import playFootballBackgroundImage from './assets/footballBackground.webp';
import playFootballBackgroundImageMobile from './assets/footballBackgroundMobile.webp';
import mainBackground from './assets/main-background.webp';
import onboardingBackground from './assets/onboarding-background.webp';
import packOpeningBackground from './assets/pack-opening-background.webp';
import replayDialogFootballBackground from './assets/replayDialog/football.webp';
import footballDesktopCountdownBackground from './assets/seasonCountdown/desktopFootballBackground.webp';
import seasonCountdownPacksFootball from './assets/seasonCountdown/packsFootball.webp';
import seasonCountdownPlayersLeftFootball from './assets/seasonCountdown/playersLeftFootball.webp';
import seasonCountdownPlayersMobileFootball from './assets/seasonCountdown/playersMobileFootball.webp';
import seasonCountdownPlayersRightFootball from './assets/seasonCountdown/playersRightFootball.webp';
import setLogoImage from './assets/set-logo.svg';
import squadFootballBoard from './assets/squad/footballBoard.webp';
import squadTeamLogos from './assets/squad/team-logos.webp';
import playVoucherIcon from './assets/voucher.svg';
import welcomeBackDialogBackground from './assets/welcomeBackDialogBackground.webp';
import welcomePackDiscounted from './assets/welcomePack/discounted.webp';
import welcomePackRegular from './assets/welcomePack/regular.webp';

type SetPlaySportAssets = {
  backgroundImage: string;
};

const DEFAULT_SET_PLAY_SPORT_ASSETS: SetPlaySportAssets = {
  backgroundImage: playFootballBackgroundImage,
};

const SET_PLAY_SPORT_ASSETS: Partial<Record<Sport, SetPlaySportAssets>> = {
  [Sport.FOOTBALL]: DEFAULT_SET_PLAY_SPORT_ASSETS,
};
const SET_PLAY_SPORT_ASSETS_MOBILE: Partial<Record<Sport, SetPlaySportAssets>> =
  {
    [Sport.FOOTBALL]: {
      backgroundImage: playFootballBackgroundImageMobile,
    },
  };

export const SET_COLORS = {
  displayName: 'Colors',
  colorTheme: 'light-mode',
  supportedSports: [Sport.FOOTBALL] as const,
  slug: 'colors',
  cardEditionSlugs: [
    'colors_standard_base',
    'colors_shiny_base',
    'colors_shiny_rising',
    'colors_holo_base',
    'colors_holo_chroma',
    'colors_full_art_base',
  ] as const,
  skins: [Skin.STANDARD, Skin.SHINY, Skin.HOLO, Skin.FULL_ART],
  colors: {
    main: 'var(--c-set-colors-surface-primary)',
    mainRGB: [123, 255, 0] satisfies [number, number, number],
    swipeEffectColor: '#000000',
  },
  background: {
    background: `url(${mainBackground}) center / cover no-repeat`,
    backgroundBlendMode: 'normal, normal',
  },
  visual: {
    board: {
      mobileDecoration,
    },
    commonPacks: {
      logoImage: commonPacksLogoImage,
    },
    cardReveal: {
      swipeIconImage: cardRevealSwipeIconImage,
      background: {
        background: `
      url("${packOpeningBackground}") center / cover no-repeat
        `,
        backgroundBlendMode: 'normal',
      },
      videoSources: null,
      newEffectColor: '0, 0, 0' as const,
    },
    seasonCountdown: {
      players: {
        [Sport.BASEBALL]: null,
        [Sport.NBA]: null,
        [Sport.FOOTBALL]: {
          mobile: seasonCountdownPlayersMobileFootball,
          desktopLeft: seasonCountdownPlayersLeftFootball,
          desktopRight: seasonCountdownPlayersRightFootball,
        },
      } satisfies Record<Sport, Record<string, string> | null>,
      backgroundMobile: undefined,
      backgroundDesktop: {
        [Sport.BASEBALL]: undefined,
        [Sport.NBA]: undefined,
        [Sport.FOOTBALL]: {
          background: `url("${footballDesktopCountdownBackground}") center / cover no-repeat`,
        },
      } satisfies Record<Sport, CSSProperties | undefined>,
      packs: {
        [Sport.BASEBALL]: null,
        [Sport.NBA]: null,
        [Sport.FOOTBALL]: seasonCountdownPacksFootball,
      } satisfies Record<Sport, string | null>,
    },
    backgroundDesktop: {
      [Sport.FOOTBALL]: {
        background: `url("${footballDesktopCountdownBackground}") center / cover no-repeat`,
      },
    },
    brand: {
      logoImage: setLogoImage,
      SetLogoImg,
      SetIcon: SetColors,
    },
    play: {
      getSportAssets: (sport: Sport, mobile?: boolean) => {
        if (mobile && SET_PLAY_SPORT_ASSETS_MOBILE[sport]) {
          return SET_PLAY_SPORT_ASSETS_MOBILE[sport];
        }
        return SET_PLAY_SPORT_ASSETS[sport] || DEFAULT_SET_PLAY_SPORT_ASSETS;
      },
      voucherIcon: playVoucherIcon,
    },
    replayDialog: {
      background: {
        [Sport.FOOTBALL]: replayDialogFootballBackground,
        [Sport.NBA]: undefined,
        [Sport.BASEBALL]: undefined,
      },
    },
    welcomePackDialog: {
      regular: {
        background: `url(${welcomePackRegular}) center / cover no-repeat`,
      },
      discounted: {
        background: `url(${welcomePackDiscounted}) center / cover no-repeat, var(--c-set-surface-primary)`,
      },
    },
    welcomeBackDialog: {
      background: `url(${welcomeBackDialogBackground}) center / cover no-repeat`,
    },
    composeTeam: {
      [Sport.FOOTBALL]: footballComposeTeam,
      [Sport.NBA]: undefined,
      [Sport.BASEBALL]: undefined,
    },
    squad: {
      boardBackground: {
        [Sport.FOOTBALL]: squadFootballBoard,
        [Sport.NBA]: squadFootballBoard,
        [Sport.BASEBALL]: undefined,
      },
      teamLogos: squadTeamLogos,
    },
    onboardingBackground: {
      background: `
        linear-gradient(
          to bottom, var(--c-nl-400) 5%, transparent
        ),
        url(${onboardingBackground}) no-repeat center / cover`,
    },
  },
  showTeamFilters: true,
} as const;
